import React, { Suspense, lazy, Dispatch, SetStateAction, useMemo, useCallback, useEffect } from 'react';
import { NewEntryDialog } from './NewEntryDialog';
import { GoToSymbolModal } from './GoToSymbolModal';
import { ReferencesModal } from './ReferencesModal';
import { PeekDefinition } from './PeekDefinition';
import { TimeMachineModal } from './TimeMachineModal';
import { ShortcutsModal } from './ShortcutsModal';
import { GistExportModal } from './GistExportModal';
import { BranchCompareModal } from './BranchCompareModal';
import { RemoteManagementModal } from './RemoteManagementModal';
import { GitHubImportModal } from './GitHubImportModal';
import { NpmImportModal } from './NpmImportModal';
import { TutorialModal } from './TutorialModal';
import { ShareLinkModal } from './ShareLinkModal';
import { EmbedCodeModal } from './EmbedCodeModal';
import { CustomThemeModal } from './CustomThemeModal';
import { PackageManagerModal } from './PackageManagerModal';
import { CloneRepoModal } from './CloneRepoModal';
import { FeatureWalkthroughModal } from './FeatureWalkthroughModal';
import { LaunchConfigModal } from './LaunchConfigModal';
import { ConflictResolutionModal } from './ConflictResolutionModal';
import { VideoTutorialsModal } from './VideoTutorialsModal';
import { ThreeWayMergeModal } from './ThreeWayMergeModal';
import { GlobalSearchPanel } from '../search/GlobalSearchPanel';
import { SideBySideDiff } from '../diff/SideBySideDiff';
import { RenameModal } from './RenameModal';
import { TreeNode, TreeIndexMap, CommandAction, NewEntryDialogState, HelpTopic } from '../../types';
import { FileTemplateKey } from '../../utils';
import { useUI, useEditorState, useSettings } from '../../contexts';
import { useSymbols, Symbol } from '../../hooks/useSymbols';
import { GitCommit, GitBranch } from '../../hooks/useGit';

const GoToFileModal = lazy(() => import('./GoToFileModal').then(m => ({ default: m.GoToFileModal })));
const CommandPalette = lazy(() => import('./CommandPalette').then(m => ({ default: m.CommandPalette })));
const HelpPanel = lazy(() => import('../help/HelpPanel').then(m => ({ default: m.HelpPanel })));

type ModalContainerProps = {
  // Help panel
  helpTopics: HelpTopic[];
  // GoTo modal
  goToResults: TreeNode[];
  handleSelectGoTo: (node: TreeNode) => void;
  // Command palette
  filteredCommands: CommandAction[];
  handleSelectCommand: (cmd: CommandAction) => void;
  // Global search
  workingFiles: Record<string, string>;
  persistedFiles: Record<string, string>;
  // Project name for exports
  projectName: string;
  onGlobalSearchSelect: (filePath: string, line: number, column: number) => void;
  // Symbol navigation
  onSymbolSelect: (symbol: Symbol) => void;
  // References
  onReferenceSelect: (filePath: string, line: number, column: number) => void;
  // Time Machine
  gitCommits: GitCommit[];
  onViewFileAtCommit: (commitId: string, filePath: string) => Promise<string | null>;
  // Branch Compare
  gitBranches: GitBranch[];
  currentBranch: string;
  // Remote Management
  gitRemotes: string[];
  // New entry dialog
  newEntryDialog: NewEntryDialogState;
  newEntryName: string;
  newEntryTemplate: FileTemplateKey;
  tree: TreeNode[];
  treeIndex: TreeIndexMap;
  setNewEntryName: (name: string) => void;
  setNewEntryTemplate: Dispatch<SetStateAction<FileTemplateKey>>;
  setNewEntryDialog: (dialog: NewEntryDialogState) => void;
  applyTreeUpdate: (nextTree: TreeNode[]) => void;
  setSelectedNodeId: (id: string | null) => void;
  setWorkingFiles: Dispatch<SetStateAction<Record<string, string>>>;
  // Conflict resolution
  conflicts?: Array<{
    path: string;
    localContent: string;
    externalContent: string;
    localTimestamp: number;
    externalTimestamp: number;
  }>;
  onResolveConflict?: (path: string, keepLocal: boolean) => void;
  onResolveAllConflicts?: (keepLocal: boolean) => void;
  // 3-Way merge
  mergeFiles?: Array<{
    path: string;
    baseContent: string;
    localContent: string;
    remoteContent: string;
  }>;
  onMergeComplete?: (path: string, mergedContent: string) => void;
  onMergeAllComplete?: (results: Array<{ path: string; content: string }>) => void;
};

export const ModalContainer = React.memo(function ModalContainer({
  helpTopics,
  goToResults,
  handleSelectGoTo,
  filteredCommands,
  handleSelectCommand,
  workingFiles,
  persistedFiles,
  projectName,
  onGlobalSearchSelect,
  onSymbolSelect,
  onReferenceSelect,
  gitCommits,
  onViewFileAtCommit,
  gitBranches,
  currentBranch,
  gitRemotes,
  newEntryDialog,
  newEntryName,
  newEntryTemplate,
  tree,
  treeIndex,
  setNewEntryName,
  setNewEntryTemplate,
  setNewEntryDialog,
  applyTreeUpdate,
  setSelectedNodeId,
  setWorkingFiles,
  conflicts = [],
  onResolveConflict,
  onResolveAllConflicts,
  mergeFiles = [],
  onMergeComplete,
  onMergeAllComplete,
}: ModalContainerProps) {
  // Get UI state from context
  const {
    goToFileOpen,
    setGoToFileOpen,
    goToQuery,
    setGoToQuery,
    goToHighlight,
    setGoToHighlight,
    goToInputRef,
    commandPaletteOpen,
    setCommandPaletteOpen,
    commandQuery,
    setCommandQuery,
    commandHighlight,
    setCommandHighlight,
    commandInputRef,
    globalSearchOpen,
    setGlobalSearchOpen,
    globalSearchMode,
    globalSearchQuery,
    globalSearchResults,
    setGlobalSearchResults,
    globalSearchLoading,
    setGlobalSearchLoading,
    goToSymbolOpen,
    setGoToSymbolOpen,
    referencesOpen,
    setReferencesOpen,
    referencesSymbol,
    referencesResults,
    referencesHighlight,
    setReferencesHighlight,
    diffViewOpen,
    setDiffViewOpen,
    diffViewFile,
    setDiffViewFile,
    renameModalOpen,
    setRenameModalOpen,
    renameSymbol,
    setRenameSymbol,
    renameNewName,
    setRenameNewName,
    peekDefinitionOpen,
    setPeekDefinitionOpen,
    peekDefinitionData,
    setPeekDefinitionData,
    timeMachineOpen,
    setTimeMachineOpen,
    timeMachineFilePath,
    shortcutsModalOpen,
    setShortcutsModalOpen,
    gistExportModalOpen,
    setGistExportModalOpen,
    branchCompareModalOpen,
    setBranchCompareModalOpen,
    remoteManagementModalOpen,
    setRemoteManagementModalOpen,
    githubImportModalOpen,
    setGithubImportModalOpen,
    npmImportModalOpen,
    setNpmImportModalOpen,
    tutorialModalOpen,
    setTutorialModalOpen,
    shareLinkModalOpen,
    setShareLinkModalOpen,
    embedCodeModalOpen,
    setEmbedCodeModalOpen,
    customThemeModalOpen,
    setCustomThemeModalOpen,
    packageManagerModalOpen,
    setPackageManagerModalOpen,
    cloneRepoModalOpen,
    setCloneRepoModalOpen,
    featureWalkthroughModalOpen,
    setFeatureWalkthroughModalOpen,
    launchConfigModalOpen,
    setLaunchConfigModalOpen,
    conflictResolutionModalOpen,
    setConflictResolutionModalOpen,
    videoTutorialsModalOpen,
    setVideoTutorialsModalOpen,
    threeWayMergeModalOpen,
    setThreeWayMergeModalOpen,
  } = useUI();

  // Get theme from settings
  const { theme } = useSettings();

  // Search through all files
  const searchFiles = useCallback(
    (query: string) => {
      if (!query || query.length < 2) return [];

      type SearchResult = {
        filePath: string;
        fileName: string;
        line: number;
        column: number;
        preview: string;
        matchStart: number;
        matchEnd: number;
      };

      const results: SearchResult[] = [];
      const lowerQuery = query.toLowerCase();
      const seen = new Set<string>();

      // Combine files
      const allFiles: { path: string; content: string }[] = [];
      Object.entries(workingFiles).forEach(([path, content]) => {
        if (!seen.has(path) && content) {
          allFiles.push({ path, content });
          seen.add(path);
        }
      });
      Object.entries(persistedFiles).forEach(([path, content]) => {
        if (!seen.has(path) && content) {
          allFiles.push({ path, content });
          seen.add(path);
        }
      });

      for (const file of allFiles) {
        const lines = file.content.split('\n');
        const fileName = file.path.split('/').pop() || file.path;

        lines.forEach((line, lineIndex) => {
          const lowerLine = line.toLowerCase();
          let startIndex = 0;

          while (true) {
            const matchIndex = lowerLine.indexOf(lowerQuery, startIndex);
            if (matchIndex === -1) break;

            const contextStart = Math.max(0, matchIndex - 30);
            const contextEnd = Math.min(line.length, matchIndex + query.length + 50);
            let preview = line.slice(contextStart, contextEnd);

            if (contextStart > 0) preview = '...' + preview;
            if (contextEnd < line.length) preview = preview + '...';

            results.push({
              filePath: file.path,
              fileName,
              line: lineIndex + 1,
              column: matchIndex + 1,
              preview: preview.trim(),
              matchStart: matchIndex - contextStart + (contextStart > 0 ? 3 : 0),
              matchEnd: matchIndex - contextStart + query.length + (contextStart > 0 ? 3 : 0),
            });

            startIndex = matchIndex + 1;
            if (results.length >= 500) break;
          }

          if (results.length >= 500) return;
        });

        if (results.length >= 500) break;
      }

      return results;
    },
    [workingFiles, persistedFiles]
  );

  // Perform search when query changes
  useEffect(() => {
    if (!globalSearchOpen || !globalSearchQuery) {
      setGlobalSearchResults([]);
      return;
    }

    setGlobalSearchLoading(true);

    const timeoutId = setTimeout(() => {
      const results = searchFiles(globalSearchQuery);
      setGlobalSearchResults(results);
      setGlobalSearchLoading(false);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [globalSearchQuery, globalSearchOpen, searchFiles, setGlobalSearchResults, setGlobalSearchLoading]);

  // Group global search results by file
  const groupedResults = useMemo(() => {
    const groups: Record<string, typeof globalSearchResults> = {};
    for (const result of globalSearchResults) {
      if (!groups[result.filePath]) {
        groups[result.filePath] = [];
      }
      groups[result.filePath].push(result);
    }
    return groups;
  }, [globalSearchResults]);

  // Get editor state from context
  const {
    code,
    activeHelpId,
    setActiveHelpId,
    setCode,
    setActiveFile,
    setOpenTabs,
  } = useEditorState();

  // Extract symbols from current code
  const { symbols } = useSymbols(code);

  // Handle symbol selection
  const handleSymbolSelect = useCallback(
    (symbol: Symbol) => {
      setGoToSymbolOpen(false);
      onSymbolSelect(symbol);
    },
    [setGoToSymbolOpen, onSymbolSelect]
  );

  // Rename state
  const [isRenaming, setIsRenaming] = React.useState(false);

  // Find all occurrences of a symbol across all files
  const renameLocations = React.useMemo(() => {
    if (!renameSymbol) return [];

    type RenameLocation = {
      filePath: string;
      line: number;
      column: number;
      length: number;
    };

    const locations: RenameLocation[] = [];
    const wordRegex = new RegExp(`\\b${renameSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');

    // Combine all files
    const allFiles = new Map<string, string>();
    Object.entries(persistedFiles).forEach(([path, content]) => {
      allFiles.set(path, content);
    });
    Object.entries(workingFiles).forEach(([path, content]) => {
      allFiles.set(path, content);
    });

    allFiles.forEach((content, filePath) => {
      const lines = content.split('\n');
      lines.forEach((line, lineIndex) => {
        let match;
        while ((match = wordRegex.exec(line)) !== null) {
          locations.push({
            filePath,
            line: lineIndex + 1,
            column: match.index + 1,
            length: renameSymbol.length,
          });
        }
        wordRegex.lastIndex = 0;
      });
    });

    return locations;
  }, [renameSymbol, workingFiles, persistedFiles]);

  // Execute rename
  const handleRenameConfirm = useCallback(() => {
    if (!renameSymbol || !renameNewName || renameNewName === renameSymbol) {
      setRenameModalOpen(false);
      return;
    }

    setIsRenaming(true);

    try {
      const updatedFiles: Record<string, string> = {};
      const wordRegex = new RegExp(`\\b${renameSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');

      // Combine all files
      const allFiles = new Map<string, string>();
      Object.entries(persistedFiles).forEach(([path, content]) => {
        allFiles.set(path, content);
      });
      Object.entries(workingFiles).forEach(([path, content]) => {
        allFiles.set(path, content);
      });

      // Replace in all files
      allFiles.forEach((content, filePath) => {
        const newContent = content.replace(wordRegex, renameNewName);
        if (newContent !== content) {
          updatedFiles[filePath] = newContent;
        }
      });

      if (Object.keys(updatedFiles).length > 0) {
        setWorkingFiles(prev => ({ ...prev, ...updatedFiles }));
      }

      setRenameModalOpen(false);
      setRenameSymbol('');
      setRenameNewName('');
    } finally {
      setIsRenaming(false);
    }
  }, [renameSymbol, renameNewName, workingFiles, persistedFiles, setWorkingFiles, setRenameModalOpen, setRenameSymbol, setRenameNewName]);

  // Cancel rename
  const handleRenameCancel = useCallback(() => {
    setRenameModalOpen(false);
    setRenameSymbol('');
    setRenameNewName('');
  }, [setRenameModalOpen, setRenameSymbol, setRenameNewName]);

  // Handle single replace in a file
  const handleReplace = useCallback(
    (filePath: string, line: number, searchText: string, replaceText: string) => {
      const content = workingFiles[filePath] || persistedFiles[filePath];
      if (!content) return;

      const lines = content.split('\n');
      if (line < 1 || line > lines.length) return;

      const lineContent = lines[line - 1];
      const lowerSearch = searchText.toLowerCase();
      const lowerLine = lineContent.toLowerCase();
      const index = lowerLine.indexOf(lowerSearch);

      if (index !== -1) {
        const originalMatch = lineContent.slice(index, index + searchText.length);
        lines[line - 1] = lineContent.slice(0, index) + replaceText + lineContent.slice(index + originalMatch.length);
        const newContent = lines.join('\n');
        setWorkingFiles(prev => ({ ...prev, [filePath]: newContent }));
      }
    },
    [workingFiles, persistedFiles, setWorkingFiles]
  );

  // Handle replace all across all files
  const handleReplaceAll = useCallback(
    (searchText: string, replaceText: string) => {
      if (!searchText) return;

      const lowerSearch = searchText.toLowerCase();
      const updatedFiles: Record<string, string> = {};

      // Process all files that have matches
      const allPaths = new Set([...Object.keys(workingFiles), ...Object.keys(persistedFiles)]);

      allPaths.forEach(path => {
        const content = workingFiles[path] || persistedFiles[path];
        if (!content) return;

        let newContent = content;
        let lowerContent = content.toLowerCase();
        let offset = 0;

        // Find and replace all occurrences
        let index = lowerContent.indexOf(lowerSearch);
        while (index !== -1) {
          const actualIndex = index + offset;
          const originalMatch = newContent.slice(actualIndex, actualIndex + searchText.length);
          newContent = newContent.slice(0, actualIndex) + replaceText + newContent.slice(actualIndex + originalMatch.length);

          // Adjust offset for length difference
          offset += replaceText.length - searchText.length;

          // Update lowercase content for next search
          lowerContent = newContent.toLowerCase();
          index = lowerContent.indexOf(lowerSearch, actualIndex + replaceText.length - offset);
        }

        if (newContent !== content) {
          updatedFiles[path] = newContent;
        }
      });

      if (Object.keys(updatedFiles).length > 0) {
        setWorkingFiles(prev => ({ ...prev, ...updatedFiles }));
      }
    },
    [workingFiles, persistedFiles, setWorkingFiles]
  );

  return (
    <>
      <Suspense fallback={null}>
        <HelpPanel topics={helpTopics} activeId={activeHelpId} onSelect={setActiveHelpId} />
      </Suspense>

      {goToFileOpen && (
        <Suspense fallback={null}>
          <GoToFileModal
            open={goToFileOpen}
            query={goToQuery}
            highlight={goToHighlight}
            results={goToResults}
            inputRef={goToInputRef}
            onClose={() => setGoToFileOpen(false)}
            onChange={(value) => {
              setGoToQuery(value);
              setGoToHighlight(0);
            }}
            onHighlight={(index) => setGoToHighlight(index)}
            onSelect={handleSelectGoTo}
          />
        </Suspense>
      )}

      {commandPaletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            open={commandPaletteOpen}
            query={commandQuery}
            highlight={commandHighlight}
            commands={filteredCommands}
            inputRef={commandInputRef}
            onClose={() => setCommandPaletteOpen(false)}
            onChange={(value) => {
              setCommandQuery(value);
              setCommandHighlight(0);
            }}
            onHighlight={(index) => setCommandHighlight(index)}
            onSelect={handleSelectCommand}
          />
        </Suspense>
      )}

      {globalSearchOpen && (
        <>
          <div className="global-search-overlay" onClick={() => setGlobalSearchOpen(false)} />
          <GlobalSearchPanel
            groupedResults={groupedResults}
            loading={globalSearchLoading}
            totalResults={globalSearchResults.length}
            mode={globalSearchMode}
            onSelectResult={onGlobalSearchSelect}
            onReplace={handleReplace}
            onReplaceAll={handleReplaceAll}
            onClose={() => setGlobalSearchOpen(false)}
          />
        </>
      )}

      {goToSymbolOpen && (
        <GoToSymbolModal
          symbols={symbols}
          onSelect={handleSymbolSelect}
          onClose={() => setGoToSymbolOpen(false)}
        />
      )}

      {diffViewOpen && diffViewFile && (
        <>
          <div className="global-search-overlay" onClick={() => {
            setDiffViewOpen(false);
            setDiffViewFile(null);
          }} />
          <div className="diff-modal">
            <SideBySideDiff
              originalContent={persistedFiles[diffViewFile] || ''}
              modifiedContent={workingFiles[diffViewFile] || persistedFiles[diffViewFile] || ''}
              originalTitle={`${diffViewFile.split('/').pop()} (original)`}
              modifiedTitle={`${diffViewFile.split('/').pop()} (modified)`}
              onClose={() => {
                setDiffViewOpen(false);
                setDiffViewFile(null);
              }}
            />
          </div>
        </>
      )}

      {referencesOpen && (
        <ReferencesModal
          isOpen={referencesOpen}
          symbolName={referencesSymbol}
          references={referencesResults}
          selectedIndex={referencesHighlight}
          onSelect={(ref) => {
            setReferencesOpen(false);
            onReferenceSelect(ref.path, ref.line, ref.column);
          }}
          onClose={() => setReferencesOpen(false)}
          onNavigate={(direction) => {
            const maxIndex = referencesResults.length - 1;
            if (direction === 'up') {
              setReferencesHighlight(referencesHighlight > 0 ? referencesHighlight - 1 : maxIndex);
            } else {
              setReferencesHighlight(referencesHighlight < maxIndex ? referencesHighlight + 1 : 0);
            }
          }}
        />
      )}

      <RenameModal
        isOpen={renameModalOpen}
        symbolName={renameSymbol}
        newName={renameNewName}
        locations={renameLocations}
        isRenaming={isRenaming}
        onNewNameChange={setRenameNewName}
        onConfirm={handleRenameConfirm}
        onCancel={handleRenameCancel}
      />

      <NewEntryDialog
        dialog={newEntryDialog}
        entryName={newEntryName}
        entryTemplate={newEntryTemplate}
        tree={tree}
        treeIndex={treeIndex}
        onChangeName={setNewEntryName}
        onChangeTemplate={setNewEntryTemplate}
        onClose={() => setNewEntryDialog({ open: false, type: 'file', parent: null })}
        onApplyTreeUpdate={applyTreeUpdate}
        setSelectedNodeId={setSelectedNodeId}
        setWorkingFiles={setWorkingFiles}
        setOpenTabs={setOpenTabs}
        setActiveFile={setActiveFile}
        setCode={setCode}
      />

      {peekDefinitionOpen && peekDefinitionData && (
        <PeekDefinition
          isOpen={peekDefinitionOpen}
          symbolName={peekDefinitionData.symbolName}
          filePath={peekDefinitionData.filePath}
          code={peekDefinitionData.code}
          line={peekDefinitionData.line}
          column={peekDefinitionData.column}
          position={peekDefinitionData.position}
          onClose={() => {
            setPeekDefinitionOpen(false);
            setPeekDefinitionData(null);
          }}
          onGoToDefinition={() => {
            setPeekDefinitionOpen(false);
            setPeekDefinitionData(null);
            onGlobalSearchSelect(peekDefinitionData.filePath, peekDefinitionData.line, peekDefinitionData.column);
          }}
        />
      )}

      <TimeMachineModal
        isOpen={timeMachineOpen}
        filePath={timeMachineFilePath}
        commits={gitCommits}
        onClose={() => setTimeMachineOpen(false)}
        onViewAtCommit={onViewFileAtCommit}
      />

      <ShortcutsModal
        isOpen={shortcutsModalOpen}
        onClose={() => setShortcutsModalOpen(false)}
      />

      <GistExportModal
        isOpen={gistExportModalOpen}
        onClose={() => setGistExportModalOpen(false)}
        files={{ ...persistedFiles, ...workingFiles }}
        projectName={projectName}
      />

      <BranchCompareModal
        isOpen={branchCompareModalOpen}
        onClose={() => setBranchCompareModalOpen(false)}
        branches={gitBranches}
        currentBranch={currentBranch}
        workingFiles={workingFiles}
        persistedFiles={persistedFiles}
        onSelectFile={(filePath) => {
          setBranchCompareModalOpen(false);
          onGlobalSearchSelect(filePath, 1, 1);
        }}
      />

      <RemoteManagementModal
        isOpen={remoteManagementModalOpen}
        onClose={() => setRemoteManagementModalOpen(false)}
        remotes={gitRemotes}
      />

      <GitHubImportModal
        isOpen={githubImportModalOpen}
        onClose={() => setGithubImportModalOpen(false)}
        onImport={(files) => {
          setWorkingFiles(prev => ({ ...prev, ...files }));
          setGithubImportModalOpen(false);
        }}
      />

      <NpmImportModal
        isOpen={npmImportModalOpen}
        onClose={() => setNpmImportModalOpen(false)}
        onImport={(files) => {
          setWorkingFiles(prev => ({ ...prev, ...files }));
          setNpmImportModalOpen(false);
        }}
      />

      <TutorialModal
        isOpen={tutorialModalOpen}
        onClose={() => setTutorialModalOpen(false)}
      />

      <ShareLinkModal
        isOpen={shareLinkModalOpen}
        onClose={() => setShareLinkModalOpen(false)}
        files={{ ...persistedFiles, ...workingFiles }}
        projectName={projectName}
      />

      <EmbedCodeModal
        isOpen={embedCodeModalOpen}
        onClose={() => setEmbedCodeModalOpen(false)}
        files={{ ...persistedFiles, ...workingFiles }}
        projectName={projectName}
      />

      <CustomThemeModal
        isOpen={customThemeModalOpen}
        onClose={() => setCustomThemeModalOpen(false)}
        currentTheme={theme}
        onApplyTheme={(customTheme) => {
          if (customTheme) {
            // Apply custom theme colors to CSS variables
            const root = document.documentElement;
            root.style.setProperty('--bg', customTheme.colors.bg);
            root.style.setProperty('--bg-secondary', customTheme.colors.bgSecondary);
            root.style.setProperty('--text', customTheme.colors.text);
            root.style.setProperty('--text-secondary', customTheme.colors.textSecondary);
            root.style.setProperty('--border', customTheme.colors.border);
            root.style.setProperty('--accent', customTheme.colors.accent);
            root.style.setProperty('--success', customTheme.colors.success);
            root.style.setProperty('--warning', customTheme.colors.warning);
            root.style.setProperty('--error', customTheme.colors.error);
            root.style.setProperty('--muted', customTheme.colors.muted);
          } else {
            // Reset to default by removing custom properties
            const root = document.documentElement;
            root.style.removeProperty('--bg');
            root.style.removeProperty('--bg-secondary');
            root.style.removeProperty('--text');
            root.style.removeProperty('--text-secondary');
            root.style.removeProperty('--border');
            root.style.removeProperty('--accent');
            root.style.removeProperty('--success');
            root.style.removeProperty('--warning');
            root.style.removeProperty('--error');
            root.style.removeProperty('--muted');
          }
        }}
      />

      <PackageManagerModal
        isOpen={packageManagerModalOpen}
        onClose={() => setPackageManagerModalOpen(false)}
        files={{ ...persistedFiles, ...workingFiles }}
        onUpdatePackageJson={(content) => {
          setWorkingFiles(prev => ({ ...prev, 'package.json': content }));
        }}
      />

      <CloneRepoModal
        isOpen={cloneRepoModalOpen}
        onClose={() => setCloneRepoModalOpen(false)}
        onImport={(files) => {
          setWorkingFiles(prev => ({ ...prev, ...files }));
          setCloneRepoModalOpen(false);
        }}
      />

      <FeatureWalkthroughModal
        isOpen={featureWalkthroughModalOpen}
        onClose={() => setFeatureWalkthroughModalOpen(false)}
      />

      <LaunchConfigModal
        isOpen={launchConfigModalOpen}
        onClose={() => setLaunchConfigModalOpen(false)}
        files={{ ...persistedFiles, ...workingFiles }}
        onUpdateLaunchConfig={(content) => {
          setWorkingFiles(prev => ({ ...prev, '.atlas-ide/launch.json': content }));
        }}
        onRunConfig={(config) => {
          // Just close the modal - actual execution would need terminal integration
          console.log('Running config:', config);
          setLaunchConfigModalOpen(false);
        }}
      />

      <ConflictResolutionModal
        isOpen={conflictResolutionModalOpen}
        onClose={() => setConflictResolutionModalOpen(false)}
        conflicts={conflicts}
        onResolve={(path, keepLocal) => {
          onResolveConflict?.(path, keepLocal);
        }}
        onResolveAll={(keepLocal) => {
          onResolveAllConflicts?.(keepLocal);
          setConflictResolutionModalOpen(false);
        }}
      />

      <VideoTutorialsModal
        isOpen={videoTutorialsModalOpen}
        onClose={() => setVideoTutorialsModalOpen(false)}
      />

      <ThreeWayMergeModal
        isOpen={threeWayMergeModalOpen}
        onClose={() => setThreeWayMergeModalOpen(false)}
        files={mergeFiles}
        onMergeComplete={(path, content) => {
          onMergeComplete?.(path, content);
        }}
        onMergeAllComplete={(results) => {
          onMergeAllComplete?.(results);
          setThreeWayMergeModalOpen(false);
        }}
      />
    </>
  );
});
